/**
 * Hover/focus/tap card for a graded pull. Shows the four sub-grades a grader
 * scores (centring, corners, edges, surface), the exact defects noted on the
 * label, and — when the card art is known — a mobile-friendly preview with the
 * defects drawn onto the art.
 *
 * Interaction model:
 *  - pointer devices: hover to peek, click to PIN the panel open
 *  - touch devices: tap to open (pinned), tap ✕ or outside to close
 *  - keyboard: Enter/Space toggles the pin, Escape closes, focus moves into the
 *    panel while it is pinned so the close button is reachable
 */
import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { DefectArtView } from "@/components/DefectOverlay";


export interface GradeDetail {
  grade: number;
  grade_label?: string;
  centering?: number | null;
  corners?: number | null;
  edges?: number | null;
  surface?: number | null;
  defects?: string[] | null;
  /** Optional card art, used for the tap preview with the defect map. */
  image?: string;
  card_name?: string;
  card_code?: string;
}

const SUBS: { key: keyof GradeDetail; label: string }[] = [
  { key: "centering", label: "Centring" },
  { key: "corners", label: "Corners" },
  { key: "edges", label: "Edges" },
  { key: "surface", label: "Surface" },
];

function tint(n: number) {
  if (n >= 10) return "#facc15";
  if (n >= 9) return "#4ade80";
  if (n >= 8) return "#60a5fa";
  if (n >= 6) return "#fb923c";
  return "#f87171";
}

export function GradeBadge({
  card,
  children,
  className = "",
}: {
  card: GradeDetail;
  /** Visible grade text — defaults to the numeric grade. */
  children?: React.ReactNode;
  className?: string;
}) {
  const [hover, setHover] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [mounted, setMounted] = useState(false);
  const id = useId();
  const wrapRef = useRef<HTMLSpanElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const open = hover || pinned;

  useEffect(() => setMounted(true), []);

  const subs = SUBS.map((s) => ({ ...s, value: Number(card[s.key] ?? 0) })).filter((s) => s.value > 0);
  const defects = (card.defects ?? []).filter(Boolean);
  const weakest = subs.length ? subs.reduce((a, b) => (b.value < a.value ? b : a)) : null;

  // Pinned panels close on Escape or on a click/tap outside.
  useEffect(() => {
    if (!pinned) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setPinned(false);
        setHover(false);
      }
    };
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (!wrapRef.current?.contains(t) && !panelRef.current?.contains(t)) {
        setPinned(false);
        setHover(false);
      }
    };

    window.addEventListener("keydown", onKey);
    window.addEventListener("pointerdown", onDown);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("pointerdown", onDown);
    };
  }, [pinned]);

  // Move focus into the pinned panel so keyboard users reach the close button.
  useEffect(() => {
    if (pinned) panelRef.current?.focus();
  }, [pinned]);

  return (
    <span ref={wrapRef} className={`relative inline-flex ${className}`}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={id}
        aria-haspopup="dialog"
        aria-label={`PalStars Grade ${card.grade.toFixed(1)}${card.card_name ? ` for ${card.card_name}` : ""} — ${
          defects.length ? `${defects.length} card issue${defects.length === 1 ? "" : "s"}` : "no defects noted"
        }. Show the grading report.`}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onFocus={() => setHover(true)}
        onBlur={() => setHover(false)}
        onClick={() => setPinned((v) => !v)}
        className="cursor-help underline decoration-dotted decoration-from-font underline-offset-2 focus-visible:outline-2 focus-visible:outline-offset-2"
        style={{ color: tint(card.grade) }}
      >
        {children ?? card.grade.toFixed(1)}
      </button>

      {mounted && open &&
        createPortal(
        <span
          ref={panelRef}
          id={id}
          role="dialog"
          aria-modal="false"
          aria-label={`Grading report — ${card.card_name ?? "card"} ${card.grade.toFixed(1)}`}
          tabIndex={-1}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          className="glass fixed left-1/2 top-3 z-[200] block max-h-[calc(100vh-1.5rem)] -translate-x-1/2 overflow-y-auto rounded-xl border p-4 text-left text-[12px] leading-snug shadow-2xl ring-1 ring-primary/20"
          style={{
            width: "min(23rem, calc(100vw - 1.5rem))",
          }}
        >

          <span className="flex items-start justify-between gap-2">
            <span className="block text-xs font-bold" style={{ color: tint(card.grade) }}>
              {card.grade_label ?? `Grade ${card.grade.toFixed(1)}`} · {card.grade.toFixed(1)}
            </span>
            <button
              type="button"
              onClick={() => { setPinned(false); setHover(false); }}
              aria-label="Close grading report"
              className="-mr-1 -mt-1 rounded p-1 text-muted-foreground hover:text-foreground"
            >
              ✕
            </button>
          </span>

          <DefectArtView card={card} image={card.image} name={card.card_name} className="mt-2" />

          {subs.length > 0 && (
            <span className="mt-2 block space-y-1">
              {subs.map((s) => (
                <span key={String(s.key)} className="flex items-center gap-2">
                  <span className="w-16 shrink-0 text-muted-foreground">{s.label}</span>
                  <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                    <span
                      className="block h-full rounded-full"
                      style={{ width: `${s.value * 10}%`, background: tint(s.value) }}
                    />
                  </span>
                  <span className="w-6 shrink-0 text-right tabular-nums" style={{ color: tint(s.value) }}>
                    {s.value.toFixed(1)}
                  </span>
                </span>
              ))}
            </span>
          )}

          <span className="mt-2 block border-t pt-2">
            <span className="block font-semibold text-muted-foreground">Card issues</span>
            {defects.length === 0 ? (
              <span className="mt-1 block text-muted-foreground">
                No defects noted{weakest && weakest.value < 10 ? ` — ${weakest.label.toLowerCase()} is the weakest sub-grade.` : " — flawless across every sub-grade."}
              </span>
            ) : (
              <span className="mt-1 block space-y-0.5">
                {defects.map((d) => (
                  <span key={d} className="flex gap-1.5 text-muted-foreground">
                    <span aria-hidden="true" className="text-amber-400">•</span>
                    <span>{d}</span>
                  </span>
                ))}
              </span>
            )}
          </span>


          <span className="mt-2 block text-[10px] text-muted-foreground">
            {pinned ? "Pinned — press Escape or ✕ to close." : "Click or tap the grade to pin this open."}
          </span>
        </span>,
        document.body,
      )}


    </span>
  );
}
